import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HomeIcon,
  Cog6ToothIcon,
  AdjustmentsHorizontalIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  InboxIcon,
  CircleStackIcon,
  UserGroupIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { DIRECTUS_INSTANCE, fetchCurrentUser } from '../../api';

function AIAgentsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [singleWidgetId, setSingleWidgetId] = useState(null);
  const isAgentScopedPath = location.pathname.startsWith('/agent/ai-agents');
  const aiAgentsBasePath = isAgentScopedPath ? '/agent/ai-agents' : '/ai-agents';
  const assistantsPath = isAgentScopedPath ? '/agent/assistants' : '/assistants';
  const datasetsPath = isAgentScopedPath ? '/agent/datasets' : '/datasets';

  const bottomMenuItems = useMemo(() => ([
    { label: t('aiAgents.menu.websiteSources'), href: `${aiAgentsBasePath}/website-sources`, icon: GlobeAltIcon, external: false },
    { label: t('aiAgents.menu.inbox'), href: assistantsPath, icon: InboxIcon, external: true },
    { label: t('aiAgents.menu.knowledge'), href: datasetsPath, icon: CircleStackIcon, external: true }
  ]), [aiAgentsBasePath, assistantsPath, datasetsPath, t]);

  useEffect(() => {
    const loadWorkspaceWidgetState = async () => {
      try {
        const token = localStorage.getItem('directus_token');
        if (!token) return;

        let workspaceId = localStorage.getItem('workspace_id');
        if (!workspaceId) {
          const userData = await fetchCurrentUser(['linked_workspace.id']);
          workspaceId = userData?.linked_workspace?.id || null;
          if (workspaceId) localStorage.setItem('workspace_id', workspaceId);
        }

        if (!workspaceId) return;

        const widgetsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields: ['id'],
            limit: 2,
            sort: '-date_updated',
            filter: JSON.stringify({
              widget_linked_workspace: { _eq: workspaceId }
            })
          }
        });

        const widgets = widgetsResponse.data?.data || [];
        setSingleWidgetId(widgets.length === 1 ? widgets[0].id : null);
      } catch (error) {
        console.error('Failed to load workspace widgets:', error);
      }
    };

    loadWorkspaceWidgetState();
  }, []);

  useEffect(() => {
    if (!singleWidgetId) return;
    if (location.pathname === aiAgentsBasePath && !location.search) {
      navigate(`${aiAgentsBasePath}/${singleWidgetId}?tab=overview`, { replace: true });
    }
  }, [singleWidgetId, location.pathname, location.search, navigate, aiAgentsBasePath]);

  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get('tab');
  const section = searchParams.get('section');
  const baseSegmentsCount = aiAgentsBasePath.split('/').filter(Boolean).length;
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentPathSegment = pathParts[baseSegmentsCount];
  const currentWidgetIdFromPath = currentPathSegment && !['create-widget', 'leads', 'website-sources'].includes(currentPathSegment)
    ? currentPathSegment
    : null;
  const activeWidgetId = singleWidgetId || currentWidgetIdFromPath;

  const topMenuItems = useMemo(() => {
    const overviewHref = activeWidgetId ? `${aiAgentsBasePath}/${activeWidgetId}?tab=overview` : `${aiAgentsBasePath}?tab=overview`;
    const settingsHref = activeWidgetId ? `${aiAgentsBasePath}/${activeWidgetId}?tab=settings` : `${aiAgentsBasePath}?tab=settings`;
    const behaviorHref = activeWidgetId ? `${aiAgentsBasePath}/${activeWidgetId}?tab=settings&section=behavior` : `${aiAgentsBasePath}?tab=settings&section=behavior`;
    const installationHref = activeWidgetId ? `${aiAgentsBasePath}/${activeWidgetId}?tab=installation` : `${aiAgentsBasePath}?tab=installation`;
    const securityHref = activeWidgetId ? `${aiAgentsBasePath}/${activeWidgetId}?tab=security` : `${aiAgentsBasePath}?tab=security`;
    const isLeadsPath = location.pathname === `${aiAgentsBasePath}/leads` || location.pathname.startsWith(`${aiAgentsBasePath}/leads/`);

    return [
      {
        label: t('aiAgents.menu.overview'),
        href: overviewHref,
        icon: HomeIcon,
        isActive: !isLeadsPath && (
          tab === 'overview' ||
          (
            !tab &&
            (
              location.pathname === aiAgentsBasePath ||
              location.pathname.startsWith(`${aiAgentsBasePath}/create-widget`) ||
              location.pathname.startsWith(`${aiAgentsBasePath}/`)
            )
          )
        )
      },
      {
        label: t('aiAgents.menu.customization'),
        href: settingsHref,
        icon: Cog6ToothIcon,
        isActive: tab === 'settings' && section !== 'behavior'
      },
      {
        label: t('aiAgents.menu.behavior'),
        href: behaviorHref,
        icon: AdjustmentsHorizontalIcon,
        isActive: tab === 'settings' && section === 'behavior'
      },
      {
        label: t('aiAgents.menu.installation'),
        href: installationHref,
        icon: CodeBracketIcon,
        isActive: tab === 'installation'
      },
      {
        label: t('aiAgents.menu.security'),
        href: securityHref,
        icon: ShieldCheckIcon,
        isActive: tab === 'security'
      },
      {
        label: t('aiAgents.menu.leads'),
        href: `${aiAgentsBasePath}/leads`,
        icon: UserGroupIcon,
        isActive: location.pathname === `${aiAgentsBasePath}/leads` || location.pathname.startsWith(`${aiAgentsBasePath}/leads/`)
      }
    ];
  }, [activeWidgetId, aiAgentsBasePath, location.pathname, tab, section, t]);

  return (
    <div className="flex min-h-screen bg-neutral-100">
      <aside className="w-64 border-l border-gray-200 bg-neutral-100 px-4 py-6 pr-0 hidden lg:flex lg:flex-col">
        <nav className="">
          {topMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={`${item.label}-${item.href}`}
                to={item.href}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  item.isActive
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-neutral-200'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
          {bottomMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={`${item.label}-${item.href}`}
                to={item.href}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-neutral-200'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.external && <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />}
              </Link>
            );
          })}
          </div>
        </nav>
      </aside>

      <section className="flex-1 min-w-0 bg-neutral-100 p-4">
        <div className="min-h-full rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <Outlet />
        </div>
      </section>
    </div>
  );
}

export default AIAgentsLayout;
